"use client";

import { useId, useState } from "react";
import { NEWSLETTER_RECAPTCHA_ACTION } from "@/lib/newsletter";
import { getRecaptchaToken, loadRecaptcha } from "@/lib/recaptcha";

/*
  The newsletter subscribe form, shared by the site-wide signup band and the
  standalone /signup page.

  This replaces SendGrid's `subscribe.js` widget, whose handler was bound from a
  `DOMContentLoaded` listener behind a one-shot latch — so the button was inert
  on any page reached through a link, and silently so, because the surrounding
  form's submit was preventDefault-ed.

  Submission goes to `/api/newsletter`, which forwards to the Quantstamp form
  handler; see that route for why it is proxied rather than posted cross-origin.
*/

type Status = { state: "idle" | "pending" } | { state: "error" | "success"; message: string };

export function NewsletterForm({
  className,
  submitLabel = "Join Us",
}: {
  className?: string;
  submitLabel?: string;
}) {
  const inputId = useId();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>({ state: "idle" });

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status.state === "pending") return;

    const trimmed = email.trim();
    if (!trimmed) {
      setStatus({ state: "error", message: "Please enter a valid email address" });
      return;
    }

    setStatus({ state: "pending" });
    try {
      // Resolves to null when reCAPTCHA is blocked or slow; the form handler
      // decides what to do about a missing token rather than trapping a
      // legitimate visitor behind a script that never loaded.
      const token = await getRecaptchaToken(NEWSLETTER_RECAPTCHA_ACTION);
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: trimmed, token }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setStatus({ state: "error", message: body.error || "Something went wrong. Please try again." });
        return;
      }
      setStatus({ state: "success", message: body.message || "Thanks for subscribing." });
      setEmail("");
    } catch {
      setStatus({ state: "error", message: "Something went wrong. Please try again." });
    }
  }

  const settled = status.state === "error" || status.state === "success";

  return (
    <form className={["newsletter-form", className].filter(Boolean).join(" ")} onSubmit={onSubmit} noValidate>
      {/* Reserved space, so a message appearing does not shift the controls. */}
      <p
        className={`newsletter-form__response newsletter-form__response--${status.state === "error" ? "error" : "success"}`}
        role="status"
        aria-live="polite"
      >
        {settled ? status.message : ""}
      </p>
      <label className="sr-only" htmlFor={inputId}>
        Email address
      </label>
      <input
        id={inputId}
        className="newsletter-form__input"
        type="email"
        name="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        /*
          This form sits in the site-wide signup band, so warming on mount would be
          no better than loading reCAPTCHA in the layout. Focus is the first point
          at which this visitor, on this page, looks like they intend to subscribe.
        */
        onFocus={() => void loadRecaptcha()}
        placeholder="Enter your email address"
        autoComplete="email"
        required
      />
      <button type="submit" className="newsletter-form__submit" disabled={status.state === "pending"}>
        {status.state === "pending" ? "Joining…" : submitLabel}
      </button>
    </form>
  );
}
